import { brandStorage as localStorage } from '@/utils/brandSession';
/**
 * Dashboard Errori Questura (AlloggiatiWeb)
 * Monitora invii, errori e permette retry
 * Visibile solo a: Admin
 */
import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  AlertTriangle, 
  CheckCircle2,
  Clock,
  XCircle,
  RefreshCw,
  Building,
  Calendar,
  User,
  FileText,
  Send,
  RotateCcw,
  AlertCircle,
  TrendingUp,
  Archive
} from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import { API } from '../../utils/config';


const QuesturaDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [errors, setErrors] = useState([]);
  const [retrying, setRetrying] = useState({});
  
  const token = localStorage.getItem('hw_token');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch stats and errors in parallel
      const [statsRes, errorsRes] = await Promise.all([
        fetch(`${API}/api/alloggiati/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API}/api/alloggiati/errors?limit=50`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      if (errorsRes.ok) {
        const errorsData = await errorsRes.json();
        setErrors(errorsData.errors || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (bookingId) => {
    setRetrying(prev => ({ ...prev, [bookingId]: true }));
    
    try {
      const res = await fetch(`${API}/api/alloggiati/retry/${bookingId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await res.json();
      
      if (data.success) {
        toast.success('Invio completato con successo!');
        // Remove from errors list
        setErrors(prev => prev.filter(e => e.booking_id !== bookingId));
        // Refresh stats
        fetchData();
      } else {
        toast.error(data.message || 'Errore nel reinvio');
      }
    } catch (error) {
      console.error('Retry failed:', error);
      toast.error('Errore nella richiesta');
    } finally {
      setRetrying(prev => ({ ...prev, [bookingId]: false }));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('it-IT', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const formatCheckIn = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('it-IT', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric'
      });
    } catch {
      return dateStr.substring(0, 10);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 p-6" data-testid="questura-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dashboard Questura</h1>
            <p className="text-gray-500 mt-1">Monitoraggio invii AlloggiatiWeb</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => window.location.href = '/admin/questura-anomalies'}
              variant="outline"
              className="flex items-center gap-2 border-red-200 text-red-700 hover:bg-red-50"
              data-testid="anomalies-link-btn"
            >
              <AlertTriangle className="w-4 h-4" />
              Controllo Anomalie
            </Button>
            <Button
              onClick={fetchData}
              variant="outline"
              className="flex items-center gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Aggiorna
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Totale Invii</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.total_submissions}</p>
                  </div>
                  <Send className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-green-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Confermati</p>
                    <p className="text-2xl font-bold text-green-600">{stats.confirmed}</p>
                  </div>
                  <CheckCircle2 className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-yellow-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">In Attesa</p>
                    <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
                  </div>
                  <Clock className="w-8 h-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-red-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Errori</p>
                    <p className="text-2xl font-bold text-red-600">{stats.errors}</p>
                  </div>
                  <XCircle className="w-8 h-8 text-red-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Additional Info */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <Building className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Immobili con Questura</p>
                    <p className="text-lg font-semibold">{stats.properties_with_alloggiati}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <TrendingUp className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Invii Anno Corrente</p>
                    <p className="text-lg font-semibold">{stats.receipts_this_year}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <Archive className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Conservazione</p>
                    <p className="text-lg font-semibold">{stats.retention_policy}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Errors List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Invii con Errori ({errors.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {errors.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-500" />
                <p>Nessun errore da gestire</p>
              </div>
            ) : (
              <div className="space-y-3">
                {errors.map((error, idx) => (
                  <div 
                    key={error.id || idx}
                    className="border rounded-lg p-4 bg-red-50 border-red-200"
                    data-testid={`error-item-${idx}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="font-semibold text-gray-900">{error.guest_name}</span>
                          <span className="text-gray-400">|</span>
                          <Building className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-600">{error.property_name}</span>
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-gray-500 mb-2">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Check-in: {formatCheckIn(error.check_in)}
                          </span>
                          {error.booking_code && (
                            <span className="flex items-center gap-1">
                              <FileText className="w-3 h-3" />
                              {error.booking_code}
                            </span>
                          )}
                        </div>

                        <div className="bg-red-100 border border-red-300 rounded px-3 py-2 mt-2">
                          <p className="text-sm text-red-700 flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <span>{error.error_message}</span>
                          </p>
                        </div>

                        <p className="text-xs text-gray-400 mt-2">
                          Tentativo: {formatDate(error.submission_date)}
                        </p>
                      </div>

                      {error.can_retry && (
                        <Button
                          onClick={() => handleRetry(error.booking_id)}
                          disabled={retrying[error.booking_id]}
                          variant="outline"
                          size="sm"
                          className="ml-4 flex items-center gap-2 bg-white hover:bg-green-50 border-green-300 text-green-700"
                          data-testid={`retry-btn-${idx}`}
                        >
                          {retrying[error.booking_id] ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <RotateCcw className="w-4 h-4" />
                          )}
                          Riprova
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default QuesturaDashboard;
